/**
 * DashboardShell — exact replica of the reference layout the user shared.
 *
 * Layout (same on all 4 roles):
 *   ┌─────────────── Greeting row ───────────────┐
 *   ┌────────────┬────────────────┬──────────────┐
 *   │  Profile   │ Quote of Day   │ Announcements│
 *   │ + Check In │                │              │
 *   └────────────┴────────────────┴──────────────┘
 *   [ KPI 1 ][ KPI 2 ][ KPI 3 ][ KPI 4 ][ KPI 5 ][ KPI 6 ]
 *   ┌────────────┬─────────────┬──────────────────┐
 *   │ slotLeft   │ slotMid     │ slotRight        │
 *   └────────────┴─────────────┴──────────────────┘
 *
 * Inputs:
 *   greeting, subtitle, user, today, quote, announcements,
 *   onCheckIn, onCheckOut, kpis[6], slotLeft, slotMid, slotRight
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Play, Square, Coffee, ArrowRight, Megaphone } from "lucide-react";
import { avatarFor, initialsOf } from "@/lib/avatar";

export default function DashboardShell({
  greeting, subtitle,
  user, today, profile,
  quote, announcements = [],
  onCheckIn, onCheckOut, busy = false,
  kpis = [], slotLeft, slotMid, slotRight,
  extraRows = [],
  testId = "dashboard",
}) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);

  const fullName = user?.full_name || profile?.full_name || "User";
  const designation = profile?.designation || user?.designation || "—";
  const role = user?.role || "";
  const checkedIn = !!today?.check_in;
  const checkedOut = !!today?.check_out;

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen" data-testid={testId}>
      {/* GREETING ROW */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-[Outfit]">
            {greeting} <span className="inline-block" style={{ animation: "wave 2.4s ease-in-out infinite" }}>👋</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
        </div>
      </div>

      {/* TOP 3-CARD ROW: Profile (with Check In/Out) + Quote of Day + Announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5 mb-5">
        {/* Profile + Check In/Out card */}
        <Card className="p-5 sm:p-6 border-slate-100 shadow-sm bg-white rounded-2xl flex flex-col items-center" data-testid="ds-profile-card">
          <div className="relative">
            <Avatar className="w-24 h-24 ring-4 ring-violet-50">
              <AvatarImage src={user?.avatar_url || avatarFor({ full_name: fullName, gender: user?.gender, id: user?.id })} alt={fullName} />
              <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-xl font-bold">{initialsOf(fullName)}</AvatarFallback>
            </Avatar>
            <span className="absolute -right-1 bottom-1 w-5 h-5 rounded-full bg-emerald-500 ring-4 ring-white" />
          </div>
          <div className="text-center mt-3">
            <div className="text-base font-bold font-[Outfit] text-slate-900">{fullName}</div>
            <div className="text-[12px] text-slate-500">{designation}</div>
            <div className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-emerald-600 font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Online
            </div>
          </div>
          <div className="text-center mt-4">
            <div className="text-3xl font-bold font-[Outfit] text-slate-900 tabular-nums tracking-tight">
              {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4 w-full">
            <button
              onClick={onCheckIn}
              disabled={busy || checkedIn}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition shadow-sm ${
                checkedIn
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-emerald-500 hover:bg-emerald-600 text-white"
              } disabled:opacity-60 disabled:cursor-not-allowed`}
              data-testid="ds-check-in"
            >
              <Play className="w-3.5 h-3.5" /> {checkedIn ? "Checked In" : "Check In"}
            </button>
            <button
              onClick={onCheckOut}
              disabled={busy || !checkedIn || checkedOut}
              className={`px-4 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 transition shadow-sm border-2 ${
                checkedOut
                  ? "bg-slate-50 text-slate-500 border-slate-200"
                  : "bg-white text-rose-600 border-rose-200 hover:bg-rose-50"
              } disabled:opacity-60 disabled:cursor-not-allowed`}
              data-testid="ds-check-out"
            >
              {checkedOut ? <Coffee className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              {checkedOut ? "Wrapped" : "Check Out"}
            </button>
          </div>
        </Card>

        {/* Quote of the Day */}
        <Card className="p-5 sm:p-6 border-violet-100 bg-gradient-to-br from-violet-50 via-fuchsia-50/60 to-violet-50/40 shadow-sm rounded-2xl relative overflow-hidden" data-testid="ds-quote-card">
          <div className="absolute top-3 left-3 text-3xl text-violet-300 leading-none font-serif">"</div>
          <div className="absolute bottom-3 right-3 text-3xl text-violet-300 leading-none font-serif rotate-180">"</div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700 mb-3 text-center">Quote of the Day</div>
          <div className="flex items-center justify-center min-h-[120px]">
            <div className="text-center max-w-md">
              <div className="text-base sm:text-lg italic text-slate-800 font-[Outfit] leading-snug">
                {quote?.text || "The best way to predict the future is to create it."}
              </div>
              {(quote?.author || true) && (
                <div className="text-[12px] text-slate-500 mt-3 font-semibold">— {quote?.author || "Peter Drucker"}</div>
              )}
            </div>
          </div>
        </Card>

        {/* Announcements */}
        <Card className="p-5 sm:p-6 border-slate-100 shadow-sm bg-white rounded-2xl" data-testid="ds-announcements-card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight">Announcements</div>
            <Link to="/announcements" className="text-[11px] font-semibold text-violet-700 hover:underline inline-flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {announcements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <Megaphone className="w-7 h-7 text-slate-300 mb-2" />
              <div className="text-[12px] text-slate-500">No announcements yet.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {announcements.slice(0, 3).map(a => (
                <div key={a.id} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center shrink-0 mt-0.5">
                    <Megaphone className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-semibold text-slate-900 truncate">{a.title}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {a.created_at ? timeAgo(a.created_at) : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* KPI ROW — 6 cards */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-5" data-testid="ds-kpis">
          {kpis.map((k, i) => (
            <Card key={i} className="p-4 sm:p-5 border-slate-100 shadow-sm bg-white rounded-2xl">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{k.label}</div>
              <div className="text-2xl sm:text-3xl font-bold font-[Outfit] text-slate-900 mt-1.5 tabular-nums leading-none">{k.value}</div>
              {k.delta && (
                <div className={`text-[10.5px] mt-1.5 font-bold ${k.delta.tone === "good" ? "text-emerald-600" : k.delta.tone === "bad" ? "text-rose-600" : "text-slate-500"}`}>
                  {k.delta.label}
                </div>
              )}
              {k.sub && <div className="text-[10px] text-slate-400 mt-0.5">{k.sub}</div>}
            </Card>
          ))}
        </div>
      )}

      {/* BOTTOM ROW — role-specific widgets */}
      {(slotLeft || slotMid || slotRight) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          <div>{slotLeft}</div>
          <div>{slotMid}</div>
          <div>{slotRight}</div>
        </div>
      )}

      {/* EXTRA ROWS — extend without breaking layout */}
      {extraRows.map((row, i) => (
        <div key={i} className={`mt-5 grid grid-cols-1 md:grid-cols-2 ${row.length === 3 ? "lg:grid-cols-3" : row.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-1"} gap-4 lg:gap-5`}>
          {row.map((item, j) => <div key={j} className={item?.span === 2 ? "lg:col-span-2" : ""}>{item?.node || item}</div>)}
        </div>
      ))}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* -----------------------------------------------------------------
   WidgetCard — used inside slotLeft/slotMid/slotRight for consistent
   widget appearance.
   ----------------------------------------------------------------- */
export function WidgetCard({ title, action, children, testId }) {
  return (
    <Card className="p-5 border-slate-100 shadow-sm bg-white rounded-2xl h-full" data-testid={testId}>
      <div className="flex items-center justify-between mb-3.5">
        <div className="text-base font-bold font-[Outfit] text-slate-900 tracking-tight">{title}</div>
        {action}
      </div>
      {children}
    </Card>
  );
}

/* -----------------------------------------------------------------
   DonutChart — simple SVG donut for attendance / location breakdown
   data: [{ label, value, color }]
   ----------------------------------------------------------------- */
export function DonutChart({ data = [], size = 140, thickness = 18, centerLabel, centerValue }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  const radius = (size - thickness) / 2;
  const cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div className="flex items-center justify-center relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={thickness} />
        {data.map((d, i) => {
          const len = (d.value / total) * circumference;
          const seg = (
            <circle
              key={i}
              cx={cx} cy={cy} r={radius}
              fill="none"
              stroke={d.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${circumference}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
            />
          );
          offset += len;
          return seg;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {centerValue !== undefined && <div className="text-2xl font-bold font-[Outfit] text-slate-900 tabular-nums leading-none">{centerValue}</div>}
        {centerLabel && <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-1">{centerLabel}</div>}
      </div>
    </div>
  );
}
